
import { useEffect, useRef } from "react";
import { Canvas } from "fabric";
import { CustomCanvas } from "@/types/fabric";

interface UseKeyboardShortcutsProps {
  canvas: Canvas | null;
  deleteSelectedObjects: () => void;
  isEnabled?: boolean;
  canUndo?: boolean;
  canRedo?: boolean;
  onUndo?: () => void;
  onRedo?: () => void;
}

export const useKeyboardShortcuts = ({
  canvas,
  deleteSelectedObjects,
  isEnabled = true,
  canUndo = false,
  canRedo = false,
  onUndo,
  onRedo
}: UseKeyboardShortcutsProps) => {
  // Track if an input element is focused
  const inputFocusedRef = useRef(false);

  useEffect(() => {
    if (!canvas || !isEnabled) return;

    const isEditingText = () => {
      return canvas.getActiveObjects().some(obj => {
        // Need to use type assertion to avoid TypeScript errors
        // as isEditing is a runtime property that exists but isn't in the type definitions
        return obj.type === 'textbox' && (obj as any).isEditing === true;
      });
    };

    const handleInputFocus = () => {
      inputFocusedRef.current = true;
    };

    const handleInputBlur = () => {
      inputFocusedRef.current = false;
    };

    // Add event listeners to track input focus state
    document.querySelectorAll('input, textarea, [contenteditable="true"]').forEach(el => {
      el.addEventListener('focus', handleInputFocus);
      el.addEventListener('blur', handleInputBlur);
    });

    const handleKeyDown = (e: KeyboardEvent) => {
      // Don't trigger shortcuts if an input is focused or if we're editing text
      if (inputFocusedRef.current || isEditingText()) {
        return;
      }

      // Delete/Backspace keys for deleting selected objects
      if ((e.key === 'Delete' || e.key === 'Backspace') && canvas.getActiveObjects().length > 0) {
        e.preventDefault();
        deleteSelectedObjects();
      }

      // Undo/Redo shortcuts (Ctrl+Z, Ctrl+Y/Shift+Ctrl+Z)
      if (e.ctrlKey && e.key.toLowerCase() === 'z' && !e.shiftKey && canUndo && onUndo) {
        e.preventDefault();
        onUndo();
      }

      if (e.ctrlKey && ((e.key.toLowerCase() === 'y') || (e.key.toLowerCase() === 'z' && e.shiftKey)) && canRedo && onRedo) {
        e.preventDefault();
        onRedo();
      }
    };

    window.addEventListener('keydown', handleKeyDown);

    // Function to update input elements listeners when DOM changes
    const updateInputListeners = () => {
      document.querySelectorAll('input, textarea, [contenteditable="true"]').forEach(el => {
        el.removeEventListener('focus', handleInputFocus);
        el.removeEventListener('blur', handleInputBlur);
        el.addEventListener('focus', handleInputFocus);
        el.addEventListener('blur', handleInputBlur);
      });
    };

    // Use MutationObserver to watch for new input elements
    const observer = new MutationObserver(updateInputListeners);
    observer.observe(document.body, { childList: true, subtree: true });

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      document.querySelectorAll('input, textarea, [contenteditable="true"]').forEach(el => {
        el.removeEventListener('focus', handleInputFocus);
        el.removeEventListener('blur', handleInputBlur);
      });
      observer.disconnect();
    };
  }, [canvas, deleteSelectedObjects, isEnabled, canUndo, canRedo, onUndo, onRedo]);
};
